/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * ClassmateVisibilityCard - opt out of appearing on classmates' class rosters
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Skeleton,
  Switch,
} from "@lemonadetraining/web-ui";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { COLLECTIONS, firebaseDB, useAppSelector } from "@lemonadetraining/web-shared";
import { isClassmateVisible } from "@lemonadetraining/contracts";
import SavedIndicator from "./SavedIndicator";

const DESCRIPTION =
  "When this is on, members of your gym can see your first name, last initial, and photo on classes you book. Nothing else is shared. When it is off, you are included in the class count but not named. Coaches and gym staff always see the full class list. If you coach a class, your full name is shown on it either way.";

const SAVED_LABEL = {
  on: "Saved · Classmates can see you're booked.",
  off: "Saved · You're hidden from classmates. You'll be counted but not named.",
};

const SAVED_DURATION_MS = 4000;

const preferencesDoc = (uid: string) =>
  doc(firebaseDB, COLLECTIONS.Users, uid, COLLECTIONS.UserSettings, "preferences");

const ClassmateVisibilityCard: React.FC = () => {
  const uid = useAppSelector((state) => state.user.user?.uid);
  const [visible, setVisible] = useState<boolean | null>(null);
  const [readFailed, setReadFailed] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [saved, setSaved] = useState(false);

  const seqRef = useRef(0);
  const loadSeqRef = useRef(0);
  const confirmedRef = useRef<boolean | null>(null);

  const load = useCallback(async () => {
    if (!uid) return;
    // Retry stays enabled while a read is in flight, so loads can overlap; a
    // stale one landing after a newer one would pair the switch with the error.
    const seq = (loadSeqRef.current += 1);
    setVisible(null);
    confirmedRef.current = null;
    try {
      const snap = await getDoc(preferencesDoc(uid));
      if (loadSeqRef.current !== seq) return;
      // An absent document and an absent key both mean visible, so the snapshot
      // is passed through the shared predicate rather than compared here.
      const stored = isClassmateVisible(snap.exists() ? snap.data() : undefined);
      confirmedRef.current = stored;
      // Cleared only on success, so a retry that fails again keeps the error
      // and its Retry button mounted rather than dropping the user's focus.
      setReadFailed(false);
      setVisible(stored);
    } catch {
      if (loadSeqRef.current !== seq) return;
      setReadFailed(true);
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = useCallback(
    async (next: boolean) => {
      if (!uid) return;

      const seq = (seqRef.current += 1);
      setVisible(next);
      setSaved(false);
      setSaveFailed(false);

      try {
        await setDoc(
          preferencesDoc(uid),
          { classmateVisibility: next, updatedAt: serverTimestamp(), updatedFrom: "web" },
          { merge: true }
        );
        // Recorded before the staleness check because the write reached the
        // server either way; a superseded success still changed what is stored.
        confirmedRef.current = next;
        if (seqRef.current !== seq) return;
        setSaved(true);
      } catch {
        if (seqRef.current !== seq) return;
        // Never the on-screen value: while writes overlap that is an optimistic
        // guess which may itself have failed, and restoring it would tell the
        // member they are hidden when the stored setting still says visible.
        setVisible(confirmedRef.current);
        setSaveFailed(true);
      }
    },
    [uid]
  );

  const hideSaved = useCallback(() => setSaved(false), []);

  return (
    <Card data-testid="classmate-visibility-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>Classmates</CardTitle>
          <SavedIndicator
            visible={saved}
            onHidden={hideSaved}
            label={visible === false ? SAVED_LABEL.off : SAVED_LABEL.on}
            durationMs={SAVED_DURATION_MS}
          />
        </div>
        <CardDescription>Control what classmates at your gym can see.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-muted/30 p-3">
          <div className="flex flex-col gap-1">
            <Label
              htmlFor="classmate-visibility"
              className="text-sm font-medium cursor-pointer text-foreground"
            >
              Show me to classmates
            </Label>
            <p id="classmate-visibility-desc" className="text-sm text-muted-foreground">
              {DESCRIPTION}
            </p>
          </div>
          {visible === null && !readFailed && (
            <Skeleton className="h-5 w-9 shrink-0 rounded-full" />
          )}
          {visible !== null && (
            <Switch
              id="classmate-visibility"
              data-testid="classmate-visibility-switch"
              checked={visible}
              onCheckedChange={handleToggle}
              aria-describedby="classmate-visibility-desc"
            />
          )}
        </div>

        {readFailed && (
          <p role="alert" className="text-sm text-destructive">
            Couldn&apos;t load this setting.{" "}
            <Button variant="link" className="h-auto p-0 text-sm" onClick={load}>
              Retry
            </Button>
          </p>
        )}

        {saveFailed && (
          <p
            role="alert"
            data-testid="classmate-visibility-error"
            className="text-sm text-destructive"
          >
            Not saved. Use the switch to try again.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default ClassmateVisibilityCard;
